import React, { useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-datepicker';
import { Checkbox, InputGroup, RadioButton, Select, SelectItem, TextArea, TextInput } from '@wfp/ui';

import 'react-datepicker/dist/react-datepicker.css';

import {
	type Country,
	FullWarningEvent,
	Impact,
	impactLabel,
	Likelihood,
	likelihoodLabel,
	type PartialFullWarningEvent,
	Risk,
	Seriousness,
	seriousnessClass,
	seriousnessLabel,
	WarningEventType,
} from '../../restapi/types';
import { isFirstEvent, loadCountries, userCanEditTitle } from '../../restapi/warningEvent';
import { RiskIcon } from '../common/RiskIcon';
import { useEWUser, useRiskLabels } from '../../hooks/home';
import { computeSeriousness } from './utils';
import { jsDate2SimpleDate } from '../../utils/SimpleDate';
import { RichTextEditor } from '../common/RichTextEditor';
import { initJSDateCompensatingLocalTimeZone } from '../../utils/utils';

import './EventForm.scss';


interface Props {
	event: FullWarningEvent;
	disabled?: boolean;
	onChange: (changes: PartialFullWarningEvent) => void;
}

function makeTitle(countries: Country[]) {
	return '' + countries.map(c => c.name).join(' & ');
}

const likelihoodChoices = [Likelihood.negligible, Likelihood.low, Likelihood.moderate, Likelihood.high, Likelihood.veryHigh];
const impactChoices = [Impact.negligible, Impact.low, Impact.moderate, Impact.high, Impact.veryHigh];
const seriousnessChoices = [Seriousness.negligible, Seriousness.low, Seriousness.moderate, Seriousness.high, Seriousness.veryHigh];

export function EventForm({event, disabled, onChange}: Props) {
	const user = useEWUser();
	const riskLabel = useRiskLabels();

	const [countries, setCountries] = useState(event.warning.countries);
	const [autoTitle, setAutoTitle] = useState(makeTitle(event.warning.countries));
	const [title, setTitle] = useState(event.warning.title || '');
	const [likelihood, setLikelihood] = useState(event.likelihood);
	const [impact, setImpact] = useState(event.impact);
	const [seriousness, setSeriousness] = useState(event.seriousness);
	const [risks, setRisks] = useState(event.warning.risks);
	const [eventDate, setEventDate] = useState(initJSDateCompensatingLocalTimeZone(event.date));
	const [description, setDescription] = useState(event.warning.description);
	const [message, setMessage] = useState(event.message);
	const [keyTakeaway, setKeyTakeaway] = useState(event.warning.keyTakeaway);
	const [background, setBackground] = useState(event.warning.background);
	const [outlook, setOutlook] = useState(event.warning.outlook);
	const [indicators, setIndicators] = useState(event.warning.indicators);
	const [eventType, setEventType] = useState(event.type);

	const isUpdate = !isFirstEvent(event);

	if (!user) {
		return null;
	}

	const canEditTitle = userCanEditTitle(user);

	return (
		<div>
			<h4 className="color-primary mt2">Countries</h4>
			<AsyncSelect
				isDisabled={disabled || isUpdate}
				className="wfp--react-select-container"
				classNamePrefix="wfp--react-select"
				isMulti
				cacheOptions
				loadOptions={loadCountries}
				getOptionValue={(c: Country) => '' + c.pk}
				getOptionLabel={(c: Country) => c.name}
				value={countries}
				onChange={v => {
					const countries = v as Country[];
					setCountries(countries);
					const changes: PartialFullWarningEvent = {warning: {countries: countries}};

					const a = makeTitle(countries);
					if (!title || (title === autoTitle)) {
						setTitle(a);
						changes.warning!.title = a;
					}
					setAutoTitle(a);  // store current auto-title for future use

					onChange(changes);
				}}
			/>

			<h4 className="mt2 color-primary">Title</h4>
			{canEditTitle ? <TextInput
				disabled={disabled || isUpdate}
				value={title}
				placeholder="Write a title here"
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					setTitle(e.target.value);
					onChange({warning: {title: e.target.value}});
				}}
			/> : <h5 className="mt">{title}</h5>}

			<Row middle="xs">
				<Col md>
					<h4 className="mt2 color-primary">Likelihood</h4>
					<Select
						disabled={disabled}
						value={likelihood}
						name="likelihood"
						className={!likelihood ? 'placeholder-color' : ''}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
							const l = Number(e.target.value) as Likelihood;
							if (l) {
								setLikelihood(l)
								const s = computeSeriousness(l, impact);
								setSeriousness(s);
								onChange({likelihood: l, seriousness: s});
							}
						}}>
						<SelectItem
							id={`likelihood-0`}
							value={'0'}
							text="Select..."
						/>
						{likelihoodChoices.map(l =>
							<SelectItem
								key={l}
								id={`likelihood-${l}`}
								value={'' + l}
								text={likelihoodLabel[l]}
							/>,
						)}
					</Select>
				</Col>

				<Col md>
					<h4 className="mt2 color-primary">Impact</h4>
					<Select
						disabled={disabled}
						value={impact}
						name="impact"
						className={!impact ? 'placeholder-color' : ''}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
							const i = Number(e.target.value) as Impact;
							if (i) {
								setImpact(i);
								const s = computeSeriousness(likelihood, i);
								setSeriousness(s);
								onChange({impact: i, seriousness: s});
							}
						}}>
						<SelectItem
							id={`impact-0`}
							value={'0'}
							text="Select..."
						/>
						{impactChoices.map(l =>
							<SelectItem
								key={l}
								id={`impact-${l}`}
								value={'' + l}
								text={impactLabel[l]}
							/>,
						)}
					</Select>
				</Col>
			</Row>

			<h4 className="mt2 color-primary">Seriousness</h4>
			<InputGroup
				labelText="Seriousness" hideLabel
				name="seriousness">
				{seriousnessChoices.map(s =>
					<RadioButton
						key={s}
						id={`seriousness-${s}`}
						name="seriousness"
						className={`seriousness-radio ${seriousnessClass[s]}`}
						value={s}
						labelText={seriousnessLabel[s]}
						checked={seriousness === s}
						disabled={true}
						onChange={e => {
							const v: Seriousness = Number(e.target.value) as Seriousness;
							setSeriousness(v);
							onChange({seriousness: v});
						}}
					/>,
				)}
			</InputGroup>

			<h4 className="mt2 color-primary">Risks</h4>
			<InputGroup
				labelText="Risks" hideLabel
				name="risks">
				{(Object.keys(riskLabel) as Risk[]).map(r =>
					<Checkbox
						key={r}
						id={`risks-${r}`}
						name="risks"
						className="risk-checkbox"
						value={r}
						labelText={<><RiskIcon risk={r}
											   seriousness={risks.includes(r) ? seriousness : Seriousness.unspecified}/>{riskLabel[r]}</>}
						checked={risks.includes(r)}
						disabled={disabled || isUpdate}
						onChange={e => {
							const others = risks.filter(o => o !== r);
							const newRisks = e.target.checked ? [...others, r] : others;
							setRisks(newRisks);
							onChange({warning: {risks: newRisks}});
						}}
					/>,
				)}
			</InputGroup>

			<h4 className="color-primary">Warning {isUpdate ? 'updated' : 'raised'} on</h4>
			<div style={{maxWidth: 200}}>
				<DatePicker
					className="wfp--input wfp--text-input"
					disabled={disabled}
					selected={eventDate}
					onChange={(d: Date) => {
						setEventDate(d);
						onChange({date: jsDate2SimpleDate(d)});
					}}
					dateFormat="MMM yyyy"
					showMonthYearPicker
				/>
			</div>

			<h4 className="mt2 color-primary">Description</h4>
			<TextArea
				disabled={disabled || isUpdate}
				value={description}
				placeholder="Write a warning description here"
				labelText="Warning description" hideLabel
				onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
					setDescription(e.target.value);
					onChange({warning: {description: e.target.value}});
				}}
			/>

			{!isUpdate && <>
				<h4 className="mt2 color-primary">Key takeaway</h4>
				<RichTextEditor
					disabled={disabled}
					value={keyTakeaway}
					onChange={text => {
						setKeyTakeaway(text);
						onChange({warning: {keyTakeaway: text}});
					}}
				/>

				<h4 className="mt2 color-primary">Background</h4>
				<RichTextEditor
					disabled={disabled}
					value={background}
					onChange={text => {
						setBackground(text);
						onChange({warning: {background: text}});
					}}
				/>

				<h4 className="mt2 color-primary">Outlook</h4>
				<RichTextEditor
					disabled={disabled}
					value={outlook}
					onChange={text => {
						setOutlook(text);
						onChange({warning: {outlook: text}});
					}}
				/>

				<h4 className="mt2 color-primary">Indicators to watch</h4>
				<RichTextEditor
					disabled={disabled}
					value={indicators}
					onChange={text => {
						setIndicators(text);
						onChange({warning: {indicators: text}});
					}}
				/>
			</>}

			<h4 className="mt2 color-primary">Updates</h4>
			<div className="left">
				<RichTextEditor
					disabled={disabled}
					value={message}
					onChange={text => {
						setMessage(text);
						onChange({message: text});
					}}
				/>
			</div>

			{(eventType !== WarningEventType.raised) && <>
				<h4 className="mt2 color-primary">Warning status</h4>
				<Checkbox
					id={`type-closed`}
					name="type"
					labelText="With this update the warning is closed"
					checked={eventType === WarningEventType.closed}
					disabled={disabled}
					onChange={() => {
						const t = eventType === WarningEventType.closed ? WarningEventType.updated : WarningEventType.closed;
						setEventType(t);
						onChange({type: t});
					}}
				/>
			</>}

		</div>
	);
}